import React, {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import CloseSvg from "../../assets/taiga-uikit/icons/close.svg?react";
import {
  _colorText,
  _size,
  _sizeText,
  _style,
  _typeInput,
} from "../../core/core";
import { Button } from "../button/button";
import { Text } from "../texts/text";
import "./Input.css";

interface InputProps {
  size?: _size;
  type?: _typeInput;
  labelOutside?: string | ReactNode;
  label?: string;
  placeholder?: string;
  description?: string;
  required?: boolean;
  error?: string;
  icon?: React.ReactNode;
  rightText?: string;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  value?: string;
  setValue?: (value: string) => void;
  focusStart?: boolean;
  autoResize?: boolean; // Новое свойство для включения автоадаптации
  validation?: () => string;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlurHandel?: (e: FocusEvent<HTMLInputElement>) => void;
}

export const Input: React.FC<InputProps> = ({
  size = _size.M,
  type = _typeInput.text,
  labelOutside,
  label,
  placeholder,
  description,
  required = false,
  error = "",
  icon,
  rightText,
  onKeyDown,
  value = "",
  setValue,
  focusStart = false,
  autoResize = false,
  validation,
  onFocus,
  onBlurHandel,
  ...props
}) => {
  const [focus, setFocus] = useState<boolean>(focusStart);
  const [messageError, setMessageError] = useState<string>(error);

  const variantsClassName = (): string => {
    return validation && messageError !== ""
      ? `input-field input-field-${size} error`
      : `input-field input-field-${size}`;
  };

  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (setValue) setValue(event.target.value);

    if (autoResize && inputRef.current) {
      inputRef.current.style.height = "auto"; // Сброс высоты
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`; // Установка новой высоты
    }
  };
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (onKeyDown) onKeyDown(e);
  };

  useEffect(() => {
    if (validation) {
      setMessageError(validation());
    }
  }, [validation, value]);

  useEffect(() => {
    if (error !== "") {
      setMessageError(error);
    }
  }, [error]);

  const onClickClear = (): void => {
    if (setValue) {
      setValue("");
      setFocus(true);
    }
  };

  return (
    <div className="field">
      {labelOutside && (
        <Text size={_sizeText.xs} color={_colorText.neutral}>
          {labelOutside}
        </Text>
      )}
      <div className={variantsClassName()}>
        <div className="field-content">
          <input
            type={type}
            value={value}
            placeholder={placeholder || label}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={onFocus}
            onBlur={onBlurHandel}
            autoFocus={focus}
            required={required}
            {...props}
          />
        </div>
        {rightText && (
          <div className="right-content">
            <Text size={_sizeText.xs}>{rightText}</Text>
          </div>
        )}
        {value && (
          <Button
            style={_style.clear}
            icon_right={<CloseSvg />}
            onClick={onClickClear}
          />
        )}
      </div>
      {description && (
        <Text size={_sizeText.xs} color={_colorText.neutral}>
          {description}
        </Text>
      )}
      {messageError && (
        <Text size={_sizeText.xs} color={_colorText.negative}>
          {messageError}
        </Text>
      )}
    </div>
  );
};
